// src/context/TutorialContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import TutorialOverlay from '../components/TutorialOverlay';
import { logger } from '../utils/logger';

const TutorialContext = createContext();

export function useTutorial() {
  return useContext(TutorialContext);
}

// Lazy-loading map — tutorial step configs are only fetched when a tutorial starts
const TUTORIAL_LOADERS = {
  main: () => import('../config/tutorialSteps').then((m) => m.tutorialSteps),
  settings: () => import('../config/settingsTutorialSteps').then((m) => m.settingsTutorialSteps),
  team: () => import('../config/teamTutorialSteps').then((m) => m.teamTutorialSteps),
  portfolio: () => import('../config/portfolioTutorialSteps').then((m) => m.portfolioTutorialSteps),
  operations: () =>
    import('../config/operationsTutorialSteps').then((m) => m.operationsTutorialSteps),
};

export function TutorialProvider({ children, firebaseUser }) {
  const [activeTutorial, setActiveTutorial] = useState(null); // which tutorial is running (e.g. 'main', 'settings')
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(true); // default true to avoid flash
  const [activeSteps, setActiveSteps] = useState([]);

  const isActive = activeTutorial !== null;

  // Load tutorial steps dynamically and activate
  const loadAndActivate = useCallback(async (name) => {
    const loader = TUTORIAL_LOADERS[name];
    if (!loader) return;
    try {
      const steps = await loader();
      setActiveSteps(steps);
      setCurrentStep(0);
      setActiveTutorial(name);
    } catch (err) {
      logger.error(`Failed to load tutorial "${name}":`, err);
    }
  }, []);

  // Check Firestore for tutorial completion status on mount
  useEffect(() => {
    if (!firebaseUser?.uid) return;

    const checkStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          const completed = data.tutorialCompleted === true;
          setHasCompleted(completed);

          // Only auto-launch main tutorial if onboarding is already done.
          // New accounts go through OnboardingWizard first — they can start
          // the app tour later via the help button in the sidebar.
          const onboardingDone =
            data.hasCompletedOnboarding === true || data.onboardingDismissed === true;
          if (!completed && !data.tutorialDismissed && onboardingDone) {
            loadAndActivate('main');
          }
        }
      } catch (err) {
        logger.error('Tutorial status check failed:', err);
      }
    };
    checkStatus();
  }, [firebaseUser?.uid, loadAndActivate]);

  // Save completion to Firestore
  const saveToFirestore = useCallback(
    async (field, value) => {
      if (!firebaseUser?.uid) return;
      try {
        const userRef = doc(db, 'users', firebaseUser.uid);
        await updateDoc(userRef, { [field]: value });
      } catch (err) {
        try {
          await setDoc(doc(db, 'users', firebaseUser.uid), { [field]: value }, { merge: true });
        } catch (e) {
          logger.error('Failed to save tutorial status:', e);
        }
      }
    },
    [firebaseUser?.uid]
  );

  // Mark current tutorial as completed
  const markCompleted = useCallback(async () => {
    const tutorialId = activeTutorial;
    setActiveTutorial(null);
    setCurrentStep(0);
    setActiveSteps([]);

    if (tutorialId === 'main') {
      setHasCompleted(true);
      await saveToFirestore('tutorialCompleted', true);
    }
    // Page-specific tutorials don't persist — they can always be re-watched
  }, [activeTutorial, saveToFirestore]);

  // Dismiss without marking completed
  const dismiss = useCallback(async () => {
    const tutorialId = activeTutorial;
    setActiveTutorial(null);
    setCurrentStep(0);
    setActiveSteps([]);

    if (tutorialId === 'main') {
      await saveToFirestore('tutorialDismissed', true);
    }
  }, [activeTutorial, saveToFirestore]);

  // Start main tutorial (from help button in sidebar)
  const startTutorial = useCallback(() => {
    loadAndActivate('main');
  }, [loadAndActivate]);

  // Start any page-specific tutorial by name
  const startPageTutorial = useCallback(
    (tutorialName) => {
      if (TUTORIAL_LOADERS[tutorialName]) {
        loadAndActivate(tutorialName);
      }
    },
    [loadAndActivate]
  );

  const handleNext = useCallback(() => {
    if (currentStep < activeSteps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, activeSteps.length]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  return (
    <TutorialContext.Provider
      value={{ isActive, hasCompleted, startTutorial, startPageTutorial, activeTutorial }}
    >
      {children}
      {isActive && activeSteps.length > 0 && (
        <TutorialOverlay
          steps={activeSteps}
          currentStep={currentStep}
          onNext={handleNext}
          onPrev={handlePrev}
          onSkip={dismiss}
          onFinish={markCompleted}
        />
      )}
    </TutorialContext.Provider>
  );
}
